import React from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useCallback } from 'react';
import { CreateEventHeaderLayoutSettings, CreateEventPayload0301, WebsiteDesignSettingsInput } from '@graphql/generated';
import { useAuth } from '@shared/components/AuthProvider';
import { getNodeEnv } from '@shared/utils/getEnvironmentVariables';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { WarningTriangle } from '@withjoy/joykit/icons';

export type DefaultTemplatePayload = Readonly<{
  template: string;
  theme: string;
  websiteDesignSettings: Readonly<WebsiteDesignSettingsInput>;
  headerLayout: Readonly<CreateEventHeaderLayoutSettings>;
}>;

const { isDevelopment } = getNodeEnv();

export const useDefaultTemplatePayload = () => {
  const defaultTemplatePayload = useIsFeatureEnabled('createWeddingDefaultThemeExperiment')[3];
  const { isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const { tq } = useTranslation('createWedding');

  const applyDefaultTemplatePayload = useCallback(
    async (payload: CreateEventPayload0301) => {
      if (defaultTemplatePayload && (isSuperAdmin || isDevelopment)) {
        const { websiteDesignSchema } = await import('./util');
        const isPayloadValid = websiteDesignSchema.isValidSync(defaultTemplatePayload);
        if (!isPayloadValid) {
          toast(tq`Invalid template settings payload`, {
            icon: <WarningTriangle />,
            position: 'top-center',
          });
          return { payload, error: true };
        }
      }

      if (defaultTemplatePayload) {
        const validatedDefaultTemplatePayload = defaultTemplatePayload as DefaultTemplatePayload;
        return {
          payload: {
            ...payload,
            template: validatedDefaultTemplatePayload.template,
            theme: validatedDefaultTemplatePayload.theme,
            websiteDesignSettings: validatedDefaultTemplatePayload.websiteDesignSettings,
            headerLayout: validatedDefaultTemplatePayload.headerLayout,
          },
          error: false,
        };
      }
      return { payload, error: false };
    },
    [defaultTemplatePayload, isSuperAdmin, tq, toast],
  );

  return applyDefaultTemplatePayload;
};
